import { db } from '../config/firebase';
import { 
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, 
  query, where, orderBy, Timestamp, setDoc, writeBatch, increment
} from 'firebase/firestore';

class CourseService {
  // Course Settings
  async getCourseSettings(bookId) {
    try {
      const settingsRef = doc(db, `pb-books/${bookId}/settings/course`);
      const settingsDoc = await getDoc(settingsRef);
      return settingsDoc.exists() ? settingsDoc.data() : null;
    } catch (error) {
      console.error('Error getting course settings:', error);
      throw error;
    }
  }

  async updateCourseSettings(bookId, settings) {
    try {
      const settingsRef = doc(db, `pb-books/${bookId}/settings/course`);
      await setDoc(settingsRef, {
        ...settings,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating course settings:', error);
      throw error;
    }
  }

  // Courses
  async createCourse(bookId, courseData) {
    try {
      const coursesRef = collection(db, `pb-books/${bookId}/courses`);
      const courseDoc = await addDoc(coursesRef, {
        ...courseData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return courseDoc.id;
    } catch (error) {
      console.error('Error creating course:', error);
      throw error;
    }
  }

  async getCourse(bookId, courseId) {
    try {
      const courseRef = doc(db, `pb-books/${bookId}/courses/${courseId}`);
      const courseDoc = await getDoc(courseRef);
      return courseDoc.exists() ? { id: courseDoc.id, ...courseDoc.data() } : null;
    } catch (error) {
      console.error('Error getting course:', error);
      throw error;
    }
  }

  async getCourses(bookId, filters = {}) {
    try {
      let coursesQuery = collection(db, `pb-books/${bookId}/courses`);
      let constraints = [];
      
      if (filters.category) {
        constraints.push(where('category', '==', filters.category));
      }
      if (filters.level) {
        constraints.push(where('level', '==', filters.level));
      }
      if (filters.isPremium !== undefined) {
        constraints.push(where('isPremium', '==', filters.isPremium));
      }
      if (filters.isPublished !== undefined) {
        constraints.push(where('isPublished', '==', filters.isPublished));
      }

      constraints.push(orderBy('createdAt', 'desc'));

      if (constraints.length > 0) {
        coursesQuery = query(coursesQuery, ...constraints);
      }

      const snapshot = await getDocs(coursesQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting courses:', error);
      throw error;
    }
  }

  async getCreatorCourses(bookId, creatorId) {
    try {
      const coursesQuery = query(
        collection(db, `pb-books/${bookId}/courses`),
        where('creatorId', '==', creatorId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(coursesQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting creator courses:', error);
      throw error;
    }
  }

  async updateCourse(bookId, courseId, courseData) {
    try {
      const courseRef = doc(db, `pb-books/${bookId}/courses/${courseId}`);
      await updateDoc(courseRef, {
        ...courseData,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating course:', error);
      throw error;
    }
  }

  async deleteCourse(bookId, courseId) {
    try {
      const courseRef = doc(db, `pb-books/${bookId}/courses/${courseId}`);
      await deleteDoc(courseRef);
    } catch (error) {
      console.error('Error deleting course:', error);
      throw error;
    }
  }

  // Lessons
  async createLesson(bookId, courseId, lessonData) {
    try {
      const lessonsRef = collection(db, `pb-books/${bookId}/courses/${courseId}/lessons`);
      const lessonDoc = await addDoc(lessonsRef, {
        ...lessonData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      // Update course lesson count
      await this.updateCourseStats(bookId, courseId);

      return lessonDoc.id;
    } catch (error) {
      console.error('Error creating lesson:', error);
      throw error;
    }
  }

  async getLesson(bookId, courseId, lessonId) {
    try {
      const lessonRef = doc(db, `pb-books/${bookId}/courses/${courseId}/lessons/${lessonId}`);
      const lessonDoc = await getDoc(lessonRef);
      return lessonDoc.exists() ? { id: lessonDoc.id, ...lessonDoc.data() } : null;
    } catch (error) {
      console.error('Error getting lesson:', error);
      throw error;
    }
  }

  async getLessons(bookId, courseId) {
    try {
      const lessonsQuery = query(
        collection(db, `pb-books/${bookId}/courses/${courseId}/lessons`),
        orderBy('order', 'asc')
      );
      const snapshot = await getDocs(lessonsQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting lessons:', error);
      throw error;
    }
  }

  async updateLesson(bookId, courseId, lessonId, lessonData) {
    try {
      const lessonRef = doc(db, `pb-books/${bookId}/courses/${courseId}/lessons/${lessonId}`);
      await updateDoc(lessonRef, {
        ...lessonData,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating lesson:', error);
      throw error;
    }
  }

  async deleteLesson(bookId, courseId, lessonId) {
    try {
      const lessonRef = doc(db, `pb-books/${bookId}/courses/${courseId}/lessons/${lessonId}`);
      await deleteDoc(lessonRef);

      // Update course stats
      await this.updateCourseStats(bookId, courseId);
    } catch (error) {
      console.error('Error deleting lesson:', error);
      throw error;
    }
  }

  async reorderLessons(bookId, courseId, lessonIds) {
    try {
      const batch = writeBatch(db);
      lessonIds.forEach((lessonId, index) => {
        const lessonRef = doc(db, `pb-books/${bookId}/courses/${courseId}/lessons/${lessonId}`);
        batch.update(lessonRef, { order: index });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error reordering lessons:', error);
      throw error;
    }
  }

  // Enrollments
  async enrollUser(bookId, courseId, userId) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/courses/${courseId}/enrollments/${userId}`);
      await setDoc(enrollmentRef, {
        userId,
        enrolledAt: Timestamp.now(),
        progress: 0,
        completedLessons: [],
        lastAccessedAt: Timestamp.now()
      });

      // Update course enrollment count
      const courseRef = doc(db, `pb-books/${bookId}/courses/${courseId}`);
      await updateDoc(courseRef, {
        enrollmentCount: increment(1)
      });
    } catch (error) {
      console.error('Error enrolling user:', error);
      throw error;
    }
  }

  async getEnrollment(bookId, courseId, userId) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/courses/${courseId}/enrollments/${userId}`);
      const enrollmentDoc = await getDoc(enrollmentRef);
      return enrollmentDoc.exists() ? enrollmentDoc.data() : null;
    } catch (error) {
      console.error('Error getting enrollment:', error);
      throw error;
    }
  }

  async getUserEnrollments(bookId, userId) {
    try {
      const coursesRef = collection(db, `pb-books/${bookId}/courses`);
      const coursesSnapshot = await getDocs(coursesRef);
      
      const enrollments = [];
      for (const courseDoc of coursesSnapshot.docs) {
        const enrollmentRef = doc(db, `pb-books/${bookId}/courses/${courseDoc.id}/enrollments/${userId}`);
        const enrollmentDoc = await getDoc(enrollmentRef);
        
        if (enrollmentDoc.exists()) {
          enrollments.push({
            courseId: courseDoc.id,
            course: { id: courseDoc.id, ...courseDoc.data() },
            ...enrollmentDoc.data()
          });
        }
      }
      
      return enrollments;
    } catch (error) {
      console.error('Error getting user enrollments:', error);
      throw error;
    }
  }

  async getUserEnrollment(bookId, userId) {
    return this.getUserEnrollments(bookId, userId);
  }


  async updateProgress(bookId, courseId, userId, lessonId) {
    try {
      const enrollmentRef = doc(db, `pb-books/${bookId}/courses/${courseId}/enrollments/${userId}`);
      const enrollmentDoc = await getDoc(enrollmentRef);
      
      if (!enrollmentDoc.exists()) {
        throw new Error('Enrollment not found');
      }

      const enrollment = enrollmentDoc.data();
      const completedLessons = enrollment.completedLessons || [];
      
      if (!completedLessons.includes(lessonId)) {
        completedLessons.push(lessonId);
      }

      // Get total lesson count
      const lessons = await this.getLessons(bookId, courseId);
      const progress = (completedLessons.length / lessons.length) * 100;

      await updateDoc(enrollmentRef, {
        completedLessons,
        progress,
        lastAccessedAt: Timestamp.now()
      });

      return { progress, completedLessons };
    } catch (error) {
      console.error('Error updating progress:', error);
      throw error;
    }
  }

  // User Progress & Profiles
  async getUserProfile(userId, bookId) {
    try {
      const profileRef = doc(db, `pb-books/${bookId}/user-profiles/${userId}`);
      const profileDoc = await getDoc(profileRef);
      return profileDoc.exists() ? profileDoc.data() : null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  async updateUserProfile(userId, bookId, profileData) {
    try {
      const profileRef = doc(db, `pb-books/${bookId}/user-profiles/${userId}`);
      await setDoc(profileRef, {
        ...profileData,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  async getUserAllProgress(userId, bookId) {
    try {
      const enrollments = await this.getUserEnrollments(bookId, userId);
      return enrollments;
    } catch (error) {
      console.error('Error getting user progress:', error);
      throw error;
    }
  }

  // Helper Methods
  async updateCourseStats(bookId, courseId) {
    try {
      const lessons = await this.getLessons(bookId, courseId);
      const totalDuration = lessons.reduce((sum, lesson) => sum + (lesson.duration || 0), 0);
      
      const courseRef = doc(db, `pb-books/${bookId}/courses/${courseId}`);
      await updateDoc(courseRef, {
        lessonCount: lessons.length,
        duration: totalDuration,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating course stats:', error);
      throw error;
    }
  }

  async getUpcomingSessions(bookId, courseId) {
    try {
      const sessionsRef = collection(db, `pb-books/${bookId}/courses/${courseId}/sessions`);
      const q = query(
        sessionsRef,
        where('startTime', '>=', Timestamp.now()),
        orderBy('startTime', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting upcoming sessions:', error);
      throw error;
    }
  }

  async getAllUpcomingSessions(bookId) {
    try {
      const coursesRef = collection(db, `pb-books/${bookId}/courses`);
      const coursesSnapshot = await getDocs(coursesRef);
      const upcomingSessions = [];

      for (const courseDoc of coursesSnapshot.docs) {
        const sessionsRef = collection(db, `pb-books/${bookId}/courses/${courseDoc.id}/sessions`);
        const q = query(
          sessionsRef,
          where('startTime', '>=', Timestamp.now()),
          orderBy('startTime', 'asc')
        );
        const sessionsSnapshot = await getDocs(q);
        sessionsSnapshot.forEach(docSnap => {
          upcomingSessions.push({
            id: docSnap.id,
            courseId: courseDoc.id,
            course: { id: courseDoc.id, ...courseDoc.data() },
            ...docSnap.data()
          });
        });
      }

      return upcomingSessions;
    } catch (error) {
      console.error('Error getting all upcoming sessions:', error);
      throw error;
    }
  }
}

export const courseService = new CourseService();